import React from 'react';
import { Badge } from 'flowbite-react';
import { IconLoader, type IconName, type IconProps } from '../iconLoader';
import { Text, TextProps } from '../text';
import { badgeTheme } from '../../styles/themes/tag';
import { ComponentSize } from '../select/__commons__/selectCommons';

export type TagThemeColor = 'primary' | 'error' | 'warning' | 'success' | 'secondary';
export type TagSize = Extract<ComponentSize, 'sm' | 'md'>;

export const iconThemeClasses: Record<TagThemeColor, string> = {
    primary: 'fill-system-on-surface',
    error: 'fill-system-on-error',
    warning: 'fill-system-on-warning-container',
    success: 'fill-system-on-success',
    secondary: 'fill-system-on-secondary',
};

const labelVariants: Record<TagSize, TextProps['variant']> = {
    sm: 'label-small',
    md: 'label-medium',
};

export type IconWithAction = IconProps & { action?: () => void; testId?: string };

export type TagProps = {
    id?: string;
    testId?: string;
    label?: string;
    color?: TagThemeColor;
    size?: TagSize;
    href?: string;
    handleClick?: () => void;
    leftIcon?: IconWithAction;
    rightIcon?: IconWithAction;
    className?: string;
};

export const Tag: React.FC<TagProps> = (props) => {
    const {
        id,
        testId,
        label = '',
        color = 'primary',
        size = 'md',
        href,
        handleClick,
        leftIcon,
        rightIcon,
        className,
    } = props;

    const LeftIcon = () => {
        return leftIcon ? (
            <IconLoader
                className={`${iconThemeClasses[color]}  ${leftIcon.action ? 'cursor-pointer' : ''} ${
                    leftIcon.className
                } shrink-0`}
                iconName={leftIcon.iconName as IconName}
                size={leftIcon.size ?? 'sm'}
                onClick={leftIcon.action}
                data-testid={leftIcon.testId}
            />
        ) : null;
    };

    const RightIcon = () => {
        return rightIcon ? (
            <IconLoader
                className={`${iconThemeClasses[color]}  ${rightIcon.action ? 'cursor-pointer' : ''} ${
                    rightIcon.className
                } shrink-0 ml-1.5`}
                size={rightIcon.size ?? 'sm'}
                iconName={rightIcon.iconName as IconName}
                onClick={rightIcon.action}
                data-testid={rightIcon.testId}
            />
        ) : null;
    };

    return (
        <Badge
            className={`${handleClick ? 'cursor-pointer' : ''} ${className}`}
            id={id}
            data-testid={testId}
            theme={badgeTheme}
            color={color}
            size={size}
            href={href}
            icon={LeftIcon}
            onClick={handleClick}
        >
            {label || rightIcon ? (
                <span className='flex flex-row items-center'>
                    <Text variant={labelVariants[size]}>{label}</Text>
                    <RightIcon />
                </span>
            ) : null}
        </Badge>
    );
};
